import { createReducer, PayloadAction } from '@reduxjs/toolkit';
import { convertArrToObj } from 'lib/src/utils/generic';
import {
    deleteSuperAdminUserFailure,
    deleteSuperAdminUserRequest,
    deleteSuperAdminUserSuccess,
    fetchAllSuperAdminUsersFailure,
    fetchAllSuperAdminUsersRequest,
    fetchAllSuperAdminUsersSuccess,
    fetchSingleSuperAdminUserFailure,
    fetchSingleSuperAdminUserRequest,
    fetchSingleSuperAdminUserSuccess,
    createSuperAdminUserFailure,
    createSuperAdminUserRequest,
    createSuperAdminUserSuccess,
    updateSuperAdminUserFailure,
    updateSuperAdminUserRequest,
    updateSuperAdminUserSuccess,
    updateSuperAdminUserPasswordRequest,
    updateSuperAdminUserPasswordSuccess,
    updateSuperAdminUserPasswordFailure,
    downgradeSuperAdminUserRequest,
    downgradeSuperAdminUserSuccess,
    downgradeSuperAdminUserFailure,
} from '@actions/superAdminUsers';
import { SuperAdminUser } from 'src/types/shared/SuperAdminUser';

interface SuperAdminUsersState {
    isFetching: boolean;
    isPosting: boolean;
    postSuccess: boolean;
    fetchError: string | null;
    postError: string | null;
    superAdminUsers: Record<number, SuperAdminUser>;
}

const initialState: SuperAdminUsersState = {
    isFetching: false,
    isPosting: false,
    postSuccess: false,
    fetchError: null,
    postError: null,
    superAdminUsers: {},
};

export default createReducer(initialState, {
    [fetchAllSuperAdminUsersRequest.type]: handleFetchRequest,
    [fetchAllSuperAdminUsersSuccess.type]: handleFetchAllSuccess,
    [fetchAllSuperAdminUsersFailure.type]: handleFetchError,
    [fetchSingleSuperAdminUserRequest.type]: handleFetchRequest,
    [fetchSingleSuperAdminUserSuccess.type]: handleFetchSingleSuccess,
    [fetchSingleSuperAdminUserFailure.type]: handleFetchError,
    [createSuperAdminUserRequest.type]: handlePostRequest,
    [createSuperAdminUserSuccess.type]: handlePostSuccess,
    [createSuperAdminUserFailure.type]: handlePostError,
    [updateSuperAdminUserRequest.type]: handlePostRequest,
    [updateSuperAdminUserSuccess.type]: handlePostSuccess,
    [updateSuperAdminUserFailure.type]: handlePostError,
    [updateSuperAdminUserPasswordRequest.type]: handlePostRequest,
    [updateSuperAdminUserPasswordSuccess.type]: handleUpdatePasswordSuccess,
    [updateSuperAdminUserPasswordFailure.type]: handlePostError,
    [deleteSuperAdminUserRequest.type]: handlePostRequest,
    [deleteSuperAdminUserFailure.type]: handlePostError,
    [deleteSuperAdminUserSuccess.type]: handleDeleteSuccess,
    [downgradeSuperAdminUserRequest.type]: handlePostRequest,
    [downgradeSuperAdminUserSuccess.type]: handlePostSuccess,
    [downgradeSuperAdminUserFailure.type]: handlePostError,
});

function handleFetchRequest(state: SuperAdminUsersState) {
    state.isFetching = true;
    state.fetchError = null;
}

function handleFetchError(state: SuperAdminUsersState, action: PayloadAction<string>) {
    state.fetchError = action.payload;
    state.isFetching = false;
}

function handleFetchAllSuccess(
    state: SuperAdminUsersState,
    action: PayloadAction<SuperAdminUser[]>,
) {
    state.superAdminUsers = convertArrToObj(action.payload);
    state.isFetching = false;
}

function handlePostRequest(state: SuperAdminUsersState) {
    state.isPosting = true;
    state.postSuccess = false;
    state.postError = null;
}

function handlePostError(state: SuperAdminUsersState, action: PayloadAction<string>) {
    state.postError = action.payload;
    state.isPosting = false;
}

function handlePostSuccess(state: SuperAdminUsersState, action: PayloadAction<SuperAdminUser>) {
    state.isPosting = false;
    state.postSuccess = true;
    state.superAdminUsers[action.payload.id] = action.payload;
}

function handleFetchSingleSuccess(
    state: SuperAdminUsersState,
    action: PayloadAction<SuperAdminUser>,
) {
    state.superAdminUsers[action.payload.id] = action.payload;
    state.isFetching = false;
}

function handleUpdatePasswordSuccess(state: SuperAdminUsersState) {
    state.isPosting = false;
    state.postSuccess = true;
}

function handleDeleteSuccess(state: SuperAdminUsersState, action: PayloadAction<number>) {
    state.isPosting = false;
    state.postSuccess = true;
    delete state.superAdminUsers[action.payload];
}
